/**
 * API client for the CCF dashboard.
 *
 * All requests include credentials (cookies). In dev, Vite proxies /api to the
 * Cloudflare Worker. In prod, the dashboard and API can share a domain or the
 * API URL is set via VITE_API_URL.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  if (res.status === 401) {
    throw new UnauthorizedError('Not authenticated');
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }

  return res.json();
}

export class UnauthorizedError extends Error {}

// === Auth ===
export const api = {
  login(token) {
    return request('/api/dash/auth/login', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },
  me() {
    return request('/api/dash/auth/me');
  },
  logout() {
    return request('/api/dash/auth/logout', { method: 'POST' });
  },

  // === Businesses ===
  listBusinesses(filters = {}) {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    return request(`/api/dash/businesses?${params}`);
  },
  getBusiness(id, campaignId) {
    const q = campaignId ? `?campaign_id=${campaignId}` : '';
    return request(`/api/dash/businesses/${id}${q}`);
  },
  getStats(campaignId) {
    const q = campaignId ? `?campaign_id=${campaignId}` : '';
    return request(`/api/dash/businesses/_stats/summary${q}`);
  },

  // === Campaigns ===
  listCampaigns() {
    return request('/api/dash/campaigns');
  },
  getCampaign(id) {
    return request(`/api/dash/campaigns/${id}`);
  },
  getCampaignStats(id) {
    return request(`/api/dash/campaigns/${id}/stats`);
  },

  // === Ideate (AI) ===
  ideate(prompt) {
    return request('/api/dash/ideate', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  },

  /**
   * Streaming ideate — consumes SSE from the API and invokes handlers as
   * events arrive. Returns a promise that resolves on 'done' or rejects on
   * network/parse error.
   *
   * handlers: { onStatus, onIntent, onCandidates, onChunk, onDone, onError }
   */
  async ideateStream(prompt, handlers = {}) {
    const res = await fetch(`${API_BASE}/api/dash/ideate/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    });

    if (res.status === 401) throw new UnauthorizedError('Not authenticated');
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Ideate stream error ${res.status}: ${text}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        let eventName = 'message';
        const dataLines = [];
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length === 0) continue;

        let payload;
        try {
          payload = JSON.parse(dataLines.join('\n'));
        } catch {
          continue;
        }

        switch (eventName) {
          case 'status':     handlers.onStatus?.(payload); break;
          case 'intent':     handlers.onIntent?.(payload); break;
          case 'candidates': handlers.onCandidates?.(payload); break;
          case 'chunk':      handlers.onChunk?.(payload); break;
          case 'done':       handlers.onDone?.(payload); return payload;
          case 'error':      handlers.onError?.(payload); throw new Error(payload.message || 'Ideate stream error');
        }
      }
    }
  },

  // === Profile ===
  getProfile() {
    return request('/api/dash/profile');
  },
  updateProfile(data) {
    return request('/api/dash/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  saveQuiz(answers) {
    return request('/api/dash/profile/quiz', {
      method: 'POST',
      body: JSON.stringify(answers),
    });
  },
  clearQuiz() {
    return request('/api/dash/profile/quiz', { method: 'DELETE' });
  },

  // === Admin ===
  listBoardMembers() {
    return request('/api/dash/admin/board-members');
  },
  createBoardMember(data) {
    return request('/api/dash/admin/board-members', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  revokeBoardMember(id) {
    return request(`/api/dash/admin/board-members/${id}/revoke`, { method: 'POST' });
  },
  activateBoardMember(id) {
    return request(`/api/dash/admin/board-members/${id}/activate`, { method: 'POST' });
  },
  regenerateInviteToken(id) {
    return request(`/api/dash/admin/board-members/${id}/regenerate`, { method: 'POST' });
  },

  // === Prospects ===
  createProspect(data) {
    return request('/api/dash/prospects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateProspect(id, data) {
    return request(`/api/dash/prospects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteProspect(id) {
    return request(`/api/dash/prospects/${id}`, { method: 'DELETE' });
  },
  listProspects(campaignId, filters = {}) {
    const params = new URLSearchParams({ campaign_id: campaignId, ...filters });
    return request(`/api/dash/prospects?${params}`);
  },
  logInteraction(prospectId, data) {
    return request(`/api/dash/prospects/${prospectId}/interactions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
